import type { GrammarTopic } from './types';
import { foundationsTopics } from './foundations';
import { everydayTopics } from './everyday';
import { modalsTopics } from './modals';
import { advancedTensesTopics } from './advanced-tenses';
import { connectingTopics } from './connecting';
import { fluencyTopics } from './fluency';
import { GRAMMAR_LEVELS, LEVEL_BY_SLUG, type GrammarLevel } from './levels';

export type GrammarGroupId =
  | 'foundations'
  | 'everyday'
  | 'modals'
  | 'advancedTenses'
  | 'connecting'
  | 'fluency';

/** A pool topic composed into the curriculum, carrying its CEFR level. */
export type GrammarTopicEntry = GrammarTopic & { level: GrammarLevel };

export type GrammarGroup = {
  /** i18n label lives at `grammarAid.groups.<id>` in the p2e namespace. */
  id: GrammarGroupId;
  topics: GrammarTopicEntry[];
};

// The group files are content POOLS (they keep the original document's
// batches); the curriculum hierarchy below is the founder's ordering and is
// composed by slug, so topics can move between groups without moving data.
const TOPIC_POOL: GrammarTopic[] = [
  ...foundationsTopics,
  ...everydayTopics,
  ...modalsTopics,
  ...advancedTensesTopics,
  ...connectingTopics,
  ...fluencyTopics,
];

const TOPIC_BY_SLUG = new Map(TOPIC_POOL.map((topic) => [topic.slug, topic]));

function topics(slugs: string[]): GrammarTopicEntry[] {
  return slugs.map((slug) => {
    const topic = TOPIC_BY_SLUG.get(slug);
    if (!topic) {
      throw new Error(`Grammar topic not found in pool: ${slug}`);
    }
    const level = LEVEL_BY_SLUG[slug];
    if (!level) {
      throw new Error(`Grammar topic without a CEFR level: ${slug}`);
    }
    return { ...topic, level };
  });
}

// Ordered from the most basic English to the most advanced — hierarchy
// reviewed with the founder (2026-10-14).
export const GRAMMAR_GROUPS: GrammarGroup[] = [
  {
    id: 'foundations',
    topics: topics([
      'glossary-of-grammar-terms',
      'cognate-words',
      'word-classification',
      'question-words',
      'verb-be-affirmative-negative',
      'verb-be-question-form',
      'simple-present',
      'adverbs-of-frequency',
      'pronouns-overview',
      'possessive-pronouns-vs-adjectives',
      'relative-pronouns',
      'omission-of-that',
      'demonstrative-adjectives',
      'there-is-there-are',
      'prepositions-of-place',
      'prepositions-of-time',
      'cardinal-numbers',
      'ordinal-numbers-dates',
    ]),
  },
  {
    id: 'everyday',
    topics: topics([
      'both-neither-either',
      'present-continuous',
      'adding-ing-gerund',
      'simple-future',
      'will-vs-going-to',
      'adverbs-of-intensity',
      'simple-past',
      'genitive-case',
      'english-vowel-sounds',
      'a-or-an',
      'talking-about-money',
      'irregular-plural-nouns',
      'countable-uncountable-nouns',
      'quantifiers',
      'how-many-vs-how-much',
      'comparatives-superlatives',
      'correlative-comparatives',
      'adjectives-ed-ing',
      'attributive-adjectives',
      'adverbs-of-degree',
      'phrasal-verbs-general',
      'phrasal-verbs-work',
    ]),
  },
  {
    id: 'modals',
    topics: topics([
      'making-polite-requests',
      'modal-verbs-overview',
      'modals-of-ability',
      'modals-of-past-ability',
      'modals-of-advice',
      'modals-of-necessity',
      'modals-of-possibility',
      'making-predictions',
      'past-modal-verbs',
    ]),
  },
  {
    id: 'advancedTenses',
    topics: topics([
      'future-continuous',
      'future-perfect',
      'past-habits-used-to-would',
      'past-continuous',
      'present-perfect',
      'transcendent-aspect',
      'past-perfect',
      'present-perfect-continuous',
      'zero-first-conditionals',
      'second-conditional',
      'third-conditional',
      'wishes-and-hopes',
      'active-passive-voice',
      'direct-reported-speech',
      'academic-writing-tenses',
    ]),
  },
  {
    id: 'connecting',
    topics: topics([
      'uses-of-rather',
      'giving-feedback',
      'expressing-opinions',
      'english-global-language',
      'sentence-connectors',
      'connectors-of-contrast',
      'connectors-cause-effect',
      'conjunctions',
    ]),
  },
  {
    id: 'fluency',
    topics: topics([
      'ed-pronunciation',
      'embedded-questions',
      'tag-questions',
      'gerunds-infinitives',
      'gerunds-infinitives-special-cases',
      'to-vs-for',
      'substitution-avoiding-repetition',
      'one-generic-pronouns',
      'causative-sentences',
      'english-collocations',
      'debating-useful-language',
    ]),
  },
];

export const ALL_GRAMMAR_TOPICS: GrammarTopicEntry[] = GRAMMAR_GROUPS.flatMap(
  (group) => group.topics,
);

/** The distinct levels a group spans, in CEFR order (e.g. ['A1', 'A2']). */
export function groupLevels(group: GrammarGroup): GrammarLevel[] {
  const present = new Set(group.topics.map((topic) => topic.level));
  return GRAMMAR_LEVELS.filter((level) => present.has(level));
}

/** A topic by slug, with its group and flat prev/next neighbours for paging. */
export function findGrammarTopic(slug: string) {
  const index = ALL_GRAMMAR_TOPICS.findIndex((topic) => topic.slug === slug);
  if (index < 0) return null;
  const group = GRAMMAR_GROUPS.find((candidate) =>
    candidate.topics.some((topic) => topic.slug === slug),
  )!;
  return {
    topic: ALL_GRAMMAR_TOPICS[index],
    group,
    prev: ALL_GRAMMAR_TOPICS[index - 1] ?? null,
    next: ALL_GRAMMAR_TOPICS[index + 1] ?? null,
  };
}

export type { GrammarTopic, GrammarBlock } from './types';
export { GRAMMAR_LEVELS } from './levels';
export type { GrammarLevel } from './levels';
export type { GrammarExercise } from './exercise-types';
export { GRAMMAR_EXERCISES } from './exercises';
